import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import brandLogo from "../logos/zoniq.png";
import { formatInr } from "./currency";
import type { InvoiceRecord } from "../types";

const INK = "#0f172a";
const MUTED = "#475569";
const BORDER = "#1f2937";
const BORDER_LIGHT = "#e2e8f0";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatInvoiceDateShort(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const month = date.toLocaleString("en-IN", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

function formatTableAmount(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatRate(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatInrTotal(value: number): string {
  return formatInr(value, { maximumFractionDigits: 2 }).replace("₹", "₹ ");
}

function stateNameCodeLine(state?: string | null, code?: string | null): string {
  const stateText = state?.trim() || "";
  const codeText = code?.trim() || "";
  return `<div>State Name: ${stateText || "&nbsp;"}&nbsp;&nbsp;&nbsp;&nbsp;Code: ${codeText || "&nbsp;"}</div>`;
}

function gstinLine(gstin?: string | null): string {
  const value = gstin?.trim() || "";
  return `<div>GSTIN/UN: ${value || "&nbsp;"}</div>`;
}

type TaxGroup = { rate: number; tax: number };

/**
 * Groups line items by their effective GST rate so a mixed-rate invoice
 * (e.g. 5% and 18% items) shows one "Tax GST n%" row per rate instead of one
 * misleading blended row. Each group's tax is `subtotal * rate / 100`, and
 * the last group absorbs any rounding remainder so the displayed rows always
 * sum to exactly `authoritativeTax` — the server's real, persisted total.
 */
function computeTaxGroups(rates: number[], amounts: number[], authoritativeTax: number): TaxGroup[] {
  const subtotalByRate = new Map<number, number>();
  rates.forEach((rate, index) => {
    const amount = amounts[index] ?? 0;
    subtotalByRate.set(rate, (subtotalByRate.get(rate) ?? 0) + amount);
  });

  const groups = Array.from(subtotalByRate.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, subtotal]) => ({
      rate,
      tax: Math.round(subtotal * rate) / 100,
    }));

  if (groups.length === 0) return [{ rate: 0, tax: authoritativeTax }];

  const drift = Math.round((authoritativeTax - groups.reduce((sum, g) => sum + g.tax, 0)) * 100) / 100;
  if (Math.abs(drift) >= 0.01) {
    const last = groups[groups.length - 1];
    last.tax = Math.round((last.tax + drift) * 100) / 100;
  }
  return groups;
}

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigitWords(n: number): string {
  if (n < 20) return ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones ? `${TENS[tens]} ${ONES[ones]}` : TENS[tens];
}

function threeDigitWords(n: number): string {
  if (n === 0) return "";
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundred) parts.push(`${ONES[hundred]} Hundred`);
  if (rest) parts.push(twoDigitWords(rest));
  return parts.join(" ");
}

function integerToIndianWords(n: number): string {
  if (n === 0) return "Zero";

  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundredPart = n % 1000;
  const parts: string[] = [];

  if (crore) parts.push(`${integerToIndianWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitWords(lakh)} Lakh`.trim());
  if (thousand) parts.push(`${threeDigitWords(thousand)} Thousand`.trim());
  if (hundredPart) parts.push(threeDigitWords(hundredPart));

  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function amountToIndianWords(value: number): string {
  const n = Number.isFinite(value) ? Math.abs(value) : 0;
  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);
  let words = `INR ${integerToIndianWords(rupees)}`;
  if (paise > 0) {
    words += ` and ${integerToIndianWords(paise)} Paise`;
  }
  return `${words} Only`;
}

/**
 * Place of supply decides the split: within the seller's state GST is levied
 * half as CGST and half as SGST/UGST; across states it is a single IGST at the
 * full rate. State code is the reliable signal (the first two digits of a
 * GSTIN); the names are a fallback for records captured before codes were.
 * With neither we assume intra-state, which is the common case and matches how
 * the blended rate was previously presented.
 */
function isInterStateSupply(options: InvoicePrintOptions): boolean {
  const sellerCode = (options.sellerStateCode || "").trim();
  const buyerCode = (options.buyerStateCode || options.consigneeStateCode || "").trim();
  if (sellerCode && buyerCode) return sellerCode !== buyerCode;

  const gstinState = (gstin?: string | null) => (gstin || "").trim().slice(0, 2);
  const sellerGstinCode = gstinState(options.sellerGstin);
  const buyerGstinCode = gstinState(options.buyerGstin ?? options.consigneeGstin);
  if (sellerGstinCode && buyerGstinCode) return sellerGstinCode !== buyerGstinCode;

  const norm = (v?: string | null) => (v || "").trim().toLowerCase();
  const sellerState = norm(options.sellerState);
  const buyerState = norm(options.buyerState ?? options.consigneeState);
  if (sellerState && buyerState) return sellerState !== buyerState;

  return false;
}

type GstSplit = {
  igstRate: number;
  igstAmount: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
};

function splitGst(rate: number, taxableValue: number, interState: boolean): GstSplit {
  const safeRate = Number.isFinite(rate) ? rate : 0;
  const safeValue = Number.isFinite(taxableValue) ? taxableValue : 0;
  if (interState) {
    return {
      igstRate: safeRate,
      igstAmount: (safeValue * safeRate) / 100,
      cgstRate: 0,
      cgstAmount: 0,
      sgstRate: 0,
      sgstAmount: 0,
    };
  }
  const half = safeRate / 2;
  const halfAmount = (safeValue * half) / 100;
  return {
    igstRate: 0,
    igstAmount: 0,
    cgstRate: half,
    cgstAmount: halfAmount,
    sgstRate: half,
    sgstAmount: halfAmount,
  };
}

function displayOrBlank(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed ? escapeHtml(trimmed) : "&nbsp;";
}

function multilineAddress(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return "&nbsp;";
  return escapeHtml(trimmed).replace(/\n/g, "<br/>");
}

export type InvoiceLineItemMeta = {
  hsn_sac?: string | null;
  tax_rate?: string | null;
};

export type InvoicePrintOptions = {
  /** Legal name of the user's business shown as seller on the invoice — never the platform. */
  sellerName?: string | null;
  /** The user's own business logo (Profile section), shown top-left — distinct from the Zoniq footer badge. */
  sellerLogoUrl?: string | null;
  eventName?: string | null;
  eventDate?: string | null;
  eventLocation?: string | null;
  logoSrc?: string;
  forPdf?: boolean;
  sellerAddress?: string | null;
  sellerGstin?: string | null;
  sellerState?: string | null;
  sellerStateCode?: string | null;
  buyerName?: string | null;
  buyerAddress?: string | null;
  buyerState?: string | null;
  buyerStateCode?: string | null;
  buyerGstin?: string | null;
  consigneeName?: string | null;
  consigneeAddress?: string | null;
  consigneeState?: string | null;
  consigneeStateCode?: string | null;
  consigneeGstin?: string | null;
  /** True when the buyer and consignee are the same — the Buyer (Bill to) block is omitted. */
  buyerSameAsConsignee?: boolean;
  /** Seller's own contact details, shown bottom-left next to the Zoniq watermark. */
  sellerPhone?: string | null;
  sellerEmail?: string | null;
  paymentTerms?: string | null;
  bankAccountHolder?: string | null;
  bankAccountNumber?: string | null;
  bankIfscCode?: string | null;
  bankBranch?: string | null;
  categoryHsn?: string | null;
  lineItemMeta?: InvoiceLineItemMeta[] | null;
};

let cachedLogoDataUrl: string | null = null;

async function resolveLogoDataUrl(): Promise<string> {
  if (cachedLogoDataUrl) return cachedLogoDataUrl;
  try {
    const response = await fetch(brandLogo);
    if (!response.ok) return brandLogo;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        cachedLogoDataUrl = reader.result as string;
        resolve(cachedLogoDataUrl);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return brandLogo;
  }
}

/**
 * The seller logo is an arbitrary URL the user pasted into their Profile —
 * possibly hosted with no CORS headers. html2canvas would either fail to
 * draw it or taint the canvas (breaking `toDataURL`), so unlike the bundled
 * brand logo, a failed fetch here just omits the logo rather than falling
 * back to anything.
 */
async function resolveExternalImageDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function invoiceDownloadFilename(invoiceNumber: string): string {
  const safe = invoiceNumber.replace(/[^\w.-]+/g, "-");
  return `Invoice-${safe}.pdf`;
}

async function waitForElementImages(root: HTMLElement): Promise<void> {
  await Promise.all(
    Array.from(root.querySelectorAll("img")).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const finish = () => resolve();
          img.addEventListener("load", finish, { once: true });
          img.addEventListener("error", finish, { once: true });
        })
    )
  );
}

const PDF_PAGE_WIDTH_PX = 794;
// A4 is 210mm x 297mm — keep the captured page's aspect ratio locked to that
// so the export is a true A4 sheet with the footer pinned to its bottom,
// however little content the invoice has.
const PDF_PAGE_HEIGHT_PX = Math.round(PDF_PAGE_WIDTH_PX * (297 / 210));

async function mountInvoiceInDocument(
  html: string,
  width = PDF_PAGE_WIDTH_PX
): Promise<{ root: HTMLDivElement; sheet: HTMLElement }> {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const sheetNode = parsed.body.querySelector(".invoice-document") ?? parsed.body.querySelector(".sheet");
  if (!(sheetNode instanceof HTMLElement)) {
    throw new Error("Invoice layout failed to render.");
  }

  const root = document.createElement("div");
  root.setAttribute("data-invoice-pdf-root", "true");
  root.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;background:#ffffff;pointer-events:none;z-index:2147483646;`;

  parsed.head.querySelectorAll("style").forEach((styleEl) => {
    root.appendChild(document.importNode(styleEl, true));
  });
  root.appendChild(document.importNode(sheetNode, true));
  document.body.appendChild(root);

  const sheet =
    root.querySelector(".invoice-document") ?? root.querySelector(".sheet");
  if (!(sheet instanceof HTMLElement)) {
    root.remove();
    throw new Error("Invoice layout failed to render.");
  }

  sheet.style.overflow = "visible";
  sheet.style.boxShadow = "none";
  sheet.style.maxWidth = `${width}px`;
  sheet.style.width = `${width}px`;
  sheet.style.margin = "0";
  const innerSheet = sheet.querySelector(".sheet");
  if (innerSheet instanceof HTMLElement) {
    innerSheet.style.boxShadow = "none";
    innerSheet.style.maxWidth = `${width}px`;
    innerSheet.style.width = `${width}px`;
  }

  await waitForElementImages(root);
  void sheet.getBoundingClientRect();
  await new Promise((resolve) => window.setTimeout(resolve, 120));

  return { root, sheet };
}

function createSinglePagePdf(canvas: HTMLCanvasElement): jsPDF {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // A flat mm margin on both axes distorts the box's aspect ratio away from
  // A4's own (210:297), which — since the source canvas is now locked to
  // that same A4 ratio via PDF_PAGE_HEIGHT_PX — left a much bigger gap below
  // the image than above it, so the footer didn't actually sit near the
  // page's bottom edge. A margin proportional to each axis keeps the inset
  // box's aspect ratio equal to the page's, so the image fills it edge to
  // edge with an even margin all round, and the footer lands right at the
  // bottom margin whether the invoice is short (page padded out to A4 by the
  // .page-footer flex push) or long (image height-fitted to the one page).
  const marginRatio = 10 / pageHeight;
  const marginX = pageWidth * marginRatio;
  const marginY = pageHeight * marginRatio;
  const maxWidth = pageWidth - marginX * 2;
  const maxHeight = pageHeight - marginY * 2;

  const aspect = canvas.width / canvas.height;
  let renderWidth = maxWidth;
  let renderHeight = renderWidth / aspect;

  if (renderHeight > maxHeight) {
    renderHeight = maxHeight;
    renderWidth = renderHeight * aspect;
  }

  const x = (pageWidth - renderWidth) / 2;
  const y = (pageHeight - renderHeight) / 2;
  const imgData = canvas.toDataURL("image/jpeg", 0.94);

  pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight, undefined, "FAST");
  return pdf;
}

async function renderInvoicePdf(html: string): Promise<jsPDF> {
  const { root, sheet } = await mountInvoiceInDocument(html, PDF_PAGE_WIDTH_PX);

  try {
    const canvas = await html2canvas(sheet, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: PDF_PAGE_WIDTH_PX,
      width: sheet.scrollWidth,
      height: sheet.scrollHeight,
    });

    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error("Invoice preview was empty. Please try again.");
    }

    return createSinglePagePdf(canvas);
  } finally {
    root.remove();
  }
}

function triggerPdfDownload(pdf: jsPDF, filename: string): void {
  pdf.save(filename);
}

function metaLineRow(label: string, value: string): string {
  return `
    <tr>
      <td class="meta-label cell-wrap">${label}</td>
      <td class="meta-value cell-wrap">${value}</td>
    </tr>`;
}

const META_SPACER_ROW = `<tr class="meta-spacer"><td colspan="2">&nbsp;</td></tr>`;

export function buildInvoicePrintHtml(
  detail: InvoiceRecord,
  options: InvoicePrintOptions = {}
): string {
  const sellerName = displayOrBlank(options.sellerName);
  const logoSrc = options.logoSrc ?? brandLogo;
  const forPdf = options.forPdf ?? false;
  const subtotal = detail.amount ?? 0;
  const tax = detail.tax ?? 0;
  const total = detail.total ?? 0;
  const invoiceDate = formatInvoiceDateShort(detail.created_at);
  const effectiveTaxRate = subtotal > 0 ? Math.round((tax / subtotal) * 10000) / 100 : 0;

  const categoryLabel = options.categoryHsn?.trim() || detail.category?.trim() || "";

  const buyerNameDisplay = displayOrBlank(options.buyerName ?? detail.vendor_name);
  const buyerAddress = options.buyerAddress?.trim() || detail.vendor_address?.trim() || "";
  const consigneeNameDisplay = displayOrBlank(options.consigneeName ?? options.buyerName ?? detail.vendor_name);
  const consigneeAddress = options.consigneeAddress?.trim() || buyerAddress;
  // Explicit flag wins; otherwise fall back to comparing the resolved display
  // values so callers that don't pass it (e.g. a bare InvoiceRecord) still work.
  const buyerSameAsConsignee =
    options.buyerSameAsConsignee ??
    (consigneeNameDisplay === buyerNameDisplay && consigneeAddress.trim() === buyerAddress.trim());

  const invoiceNumberDisplay = detail.invoice_number || "DRAFT";
  const metaVal = (v?: string | null) => (v?.trim() ? escapeHtml(v.trim()) : "&nbsp;");

  const sellerLogoHtml = options.sellerLogoUrl?.trim()
    ? `<img class="seller-logo" src="${escapeHtml(options.sellerLogoUrl.trim())}" alt="" />`
    : "";

  const eventNameVal = metaVal(options.eventName);
  const eventDateVal = options.eventDate?.trim() ? formatInvoiceDateShort(options.eventDate) : "&nbsp;";
  const eventLocationVal = metaVal(options.eventLocation);

  const lineItems = detail.line_items ?? [];
  // Each line's own rate wins; fall back to the form snapshot (older
  // invoices generated before line items carried their own rate), then to
  // the invoice's blended rate as a last resort.
  const lineItemRates = lineItems.map((item, index) => {
    if (item.tax_rate !== null && item.tax_rate !== undefined && Number.isFinite(Number(item.tax_rate))) {
      return Number(item.tax_rate);
    }
    const metaRate = options.lineItemMeta?.[index]?.tax_rate?.trim();
    if (metaRate) {
      const parsed = Number(metaRate);
      if (Number.isFinite(parsed)) return parsed;
    }
    return effectiveTaxRate;
  });
  const taxGroups = computeTaxGroups(
    lineItemRates,
    lineItems.map((item) => item.amount ?? 0),
    tax
  );
  const taxRowsHtml = taxGroups
    .map(
      (group) => `
        <tr class="tax-row">
          <td colspan="4" class="total-label">Tax GST ${formatRate(group.rate)}% :</td>
          <td class="num">${formatTableAmount(group.tax)}</td>
        </tr>`
    )
    .join("");
  const lineItemsHtml = lineItems
    .map((item, index) => {
      const meta = options.lineItemMeta?.[index];
      const hsnRaw = meta?.hsn_sac?.trim() || categoryLabel;
      const hsn = hsnRaw ? escapeHtml(hsnRaw) : "&nbsp;";
      const title = categoryLabel || item.description;
      const detailLine = categoryLabel
        ? `${item.description}, Rate ${formatRate(item.unit_price)}`
        : `Rate ${formatRate(item.unit_price)}`;
      const particulars = `<strong>${escapeHtml(title)}</strong><br/><em>${escapeHtml(detailLine)}</em>`;
      return `
        <tr class="item-row">
          <td class="particulars">${particulars}</td>
          <td class="center">${hsn}</td>
          <td class="num">${item.quantity}</td>
          <td class="num">${formatRate(lineItemRates[index])}%</td>
          <td class="num">${formatTableAmount(item.amount)}</td>
        </tr>`;
    })
    .join("");

  const emptyLineItemRow =
    lineItems.length === 0
      ? `<tr class="item-row"><td class="particulars" colspan="5">&nbsp;</td></tr>`
      : "";

  const fillerRowCount = Math.max(0, 2 - lineItems.length - (lineItems.length === 0 ? 1 : 0));
  const fillerRows = Array.from({ length: fillerRowCount }, () =>
    `<tr class="filler-row"><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>`
  ).join("");

  const sellerGstinDisplay = displayOrBlank(options.sellerGstin);

  const consigneeBlockHtml = `
            <div class="party-block-top">
              <div class="party-label">Consignee (Ship to)</div>
              <div class="party-name">${consigneeNameDisplay}</div>
              <div>${multilineAddress(consigneeAddress)}</div>
            </div>
            <div class="party-block-bottom">
              ${gstinLine(options.consigneeGstin)}
              ${stateNameCodeLine(options.consigneeState, options.consigneeStateCode)}
            </div>`;

  const buyerBlockHtml = `
            <div class="party-block-top">
              <div class="party-label">Buyer (Bill to)</div>
              <div class="party-name">${buyerNameDisplay}</div>
              <div>${multilineAddress(buyerAddress)}</div>
            </div>
            <div class="party-block-bottom">
              ${gstinLine(options.buyerGstin)}
              ${stateNameCodeLine(options.buyerState, options.buyerStateCode)}
            </div>`;

  // Side-by-side in one row when they differ; the Buyer cell is dropped
  // entirely (not just hidden) — Consignee spans the full row — when the
  // two are the same party.
  const partySectionHtml = buyerSameAsConsignee
    ? `
        <tr>
          <td class="party-block" colspan="2"><div class="party-block-inner">${consigneeBlockHtml}</div></td>
        </tr>`
    : `
        <tr>
          <td class="party-block"><div class="party-block-inner">${consigneeBlockHtml}</div></td>
          <td class="party-block"><div class="party-block-inner">${buyerBlockHtml}</div></td>
        </tr>`;

  // PDF mode renders the same markup through html2canvas at A4 width, so the
  // only differences wanted here are page chrome: no page background or padding,
  // and a sheet that fills the page height. Type sizes deliberately match the
  // preview so the downloaded file looks like what was on screen.
  const pdfCompactStyles = forPdf
    ? `
    body { padding: 0 !important; background: #fff !important; }
    .invoice-document {
      width: ${PDF_PAGE_WIDTH_PX}px !important;
      max-width: ${PDF_PAGE_WIDTH_PX}px !important;
      min-height: ${PDF_PAGE_HEIGHT_PX}px;
      display: flex !important;
      flex-direction: column;
    }
    .sheet {
      width: ${PDF_PAGE_WIDTH_PX}px !important;
      max-width: ${PDF_PAGE_WIDTH_PX}px !important;
      min-height: ${PDF_PAGE_HEIGHT_PX}px;
      box-shadow: none !important;
      overflow: visible !important;
    }
    .zoniq-footer { margin-top: auto; }
    `
    : "";

  const interState = isInterStateSupply(options);
  const placeOfSupply =
    options.buyerState?.trim() ||
    options.consigneeState?.trim() ||
    options.sellerState?.trim() ||
    "";

  // One table row per line item, each carrying its own SAC and tax split.
  type RowTotals = { taxable: number; exempt: number; igst: number; cgst: number; sgst: number };
  const rowTotals: RowTotals = { taxable: 0, exempt: 0, igst: 0, cgst: 0, sgst: 0 };

  const gstRowsHtml = lineItems
    .map((item, index) => {
      const meta = options.lineItemMeta?.[index];
      const sac = (meta?.hsn_sac?.trim() || categoryLabel || "").trim();
      const rate = lineItemRates[index] ?? 0;
      const taxableValue = item.amount ?? 0;
      // A zero-rated line is exempt rather than taxable, and the format keeps
      // those values in their own column (IndiGo uses it for airport charges).
      const isExempt = rate <= 0;
      const taxable = isExempt ? 0 : taxableValue;
      const exempt = isExempt ? taxableValue : 0;
      const split = splitGst(rate, taxable, interState);

      rowTotals.taxable += taxable;
      rowTotals.exempt += exempt;
      rowTotals.igst += split.igstAmount;
      rowTotals.cgst += split.cgstAmount;
      rowTotals.sgst += split.sgstAmount;

      const inclTotal = taxable + exempt + split.igstAmount + split.cgstAmount + split.sgstAmount;

      return `
          <tr>
            <td class="gst-desc">${escapeHtml(item.description || categoryLabel || "")}</td>
            <td class="gst-c">${sac ? escapeHtml(sac) : "&nbsp;"}</td>
            <td class="gst-n">${formatTableAmount(taxable)}</td>
            <td class="gst-n">${formatTableAmount(exempt)}</td>
            <td class="gst-n">${formatTableAmount(taxable + exempt)}</td>
            <td class="gst-n">${formatRate(split.igstRate)}</td>
            <td class="gst-n">${formatTableAmount(split.igstAmount)}</td>
            <td class="gst-n">${formatRate(split.cgstRate)}</td>
            <td class="gst-n">${formatTableAmount(split.cgstAmount)}</td>
            <td class="gst-n">${formatRate(split.sgstRate)}</td>
            <td class="gst-n">${formatTableAmount(split.sgstAmount)}</td>
            <td class="gst-n">0</td>
            <td class="gst-n">0.00</td>
            <td class="gst-n">${formatTableAmount(inclTotal)}</td>
          </tr>`;
    })
    .join("");

  const emptyGstRow =
    lineItems.length === 0
      ? `<tr><td class="gst-desc" colspan="14">&nbsp;</td></tr>`
      : "";

  const grandTaxableTotal = rowTotals.taxable + rowTotals.exempt;
  const grandInclTotal = grandTaxableTotal + rowTotals.igst + rowTotals.cgst + rowTotals.sgst;

  // The notes are the format's fixed legal block; anything the user typed on the
  // invoice is appended so it is not lost.
  const invoiceNotes = detail.notes?.trim();
  const notesHtml = [
    "Amounts have been rounded off to the nearest rupee where applicable.",
    interState
      ? "This is an inter-state supply; IGST is charged at the applicable rate."
      : "This is an intra-state supply; GST is charged as CGST and SGST/UGST in equal parts.",
    "Taxable value excludes any exempted or non-taxable charges shown separately above.",
    options.paymentTerms?.trim()
      ? `Payment terms: ${escapeHtml(options.paymentTerms.trim())}`
      : "Payment is due as per the agreed terms.",
    "This invoice is issued against the event and services described above.",
    invoiceNotes ? escapeHtml(invoiceNotes) : "",
  ]
    .filter(Boolean)
    .map((note, i) => `<li><span class="note-n">${i + 1}.</span> ${note}</li>`)
    .join("");

  const bankRows = [
    options.bankAccountHolder?.trim() ? `A/c Holder: ${escapeHtml(options.bankAccountHolder.trim())}` : "",
    options.bankAccountNumber?.trim() ? `A/c No: ${escapeHtml(options.bankAccountNumber.trim())}` : "",
    options.bankIfscCode?.trim() ? `IFSC: ${escapeHtml(options.bankIfscCode.trim())}` : "",
    options.bankBranch?.trim() ? `Branch: ${escapeHtml(options.bankBranch.trim())}` : "",
  ].filter(Boolean);

  const sellerContactLine = [
    options.sellerPhone?.trim() ? `T ${escapeHtml(options.sellerPhone.trim())}` : "",
    options.sellerEmail?.trim() ? `Email: ${escapeHtml(options.sellerEmail.trim())}` : "",
  ]
    .filter(Boolean)
    .join(". ");

  const amountWords = amountToIndianWords(grandInclTotal);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Tax Invoice ${escapeHtml(invoiceNumberDisplay)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }

    @page { size: A4; margin: 8mm; }

    html { color-scheme: light; }

    /* The reference document is set in Arial throughout, at a small fixed size
       with tight leading; everything below follows it rather than the app's
       own type scale. */
    body {
      margin: 0;
      padding: 16px;
      background: #f1f5f9;
      color: #000;
      font-family: Arial, Helvetica, "Liberation Sans", sans-serif;
      font-size: 11px;
      line-height: 1.35;
      -webkit-font-smoothing: antialiased;
    }

    .invoice-document { display: flex; justify-content: center; }

    .sheet {
      width: ${PDF_PAGE_WIDTH_PX}px;
      max-width: 100%;
      min-height: ${PDF_PAGE_HEIGHT_PX}px;
      background: #fff;
      border: 1px solid #000;
      padding: 14px 16px 12px;
      display: flex;
      flex-direction: column;
    }

    /* ---- masthead: logo left, title centred, supplier right ---- */
    .mast { display: flex; align-items: flex-start; gap: 10px; }
    .mast__logo { flex: 0 0 120px; }
    .mast__logo img { max-width: 116px; max-height: 46px; display: block; }
    .mast__title { flex: 1 1 auto; text-align: center; padding-top: 2px; }
    .mast__title-main {
      color: #1414b8;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.2;
    }
    .mast__title-sub { font-size: 10.5px; font-weight: 700; }
    .mast__supplier { flex: 0 0 290px; text-align: right; }
    .mast__supplier-name { font-weight: 700; font-size: 11.5px; }
    .mast__supplier-gstin { font-weight: 700; margin-top: 2px; }

    /* ---- the stacked label : value lines under the masthead ---- */
    .facts { margin-top: 14px; }
    .facts__row { margin-bottom: 8px; }
    .facts__label { font-weight: 700; }
    /* fixed column so "Number :" and "Date :" line up without padding hacks */
    .facts__row--pair { display: flex; gap: 0; }
    .facts__row--pair .facts__label { flex: 0 0 150px; }
    .facts__inline { display: flex; flex-wrap: wrap; gap: 0 26px; }
    .currency-line { text-align: right; font-weight: 700; margin: 8px 0 6px; }

    /* ---- the tax table ---- */
    .gst-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .gst-table th, .gst-table td {
      border: 1px solid #000;
      padding: 5px 3px;
      font-size: 9.5px;
      vertical-align: middle;
    }
    .gst-table thead th {
      font-size: 9px;
      font-weight: 700;
      text-align: center;
      line-height: 1.15;
    }
    .gst-desc { text-align: left; font-weight: 700; }
    .gst-c { text-align: center; }
    /* Indian-format lakh/crore amounts are the widest thing in the table;
       a step down plus nowrap keeps them inside their column rules. */
    .gst-table td.gst-n,
    .gst-n {
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-size: 8.5px;
      white-space: nowrap;
    }
    .gst-table tbody td { font-weight: 700; }
    .gst-total td { font-weight: 700; }
    .gst-total .gst-total-label { text-align: center; font-size: 10.5px; }

    /* ---- notes, signature, registered office ---- */
    .notes { margin-top: 12px; padding-left: 0; list-style: none; }
    .notes li { margin-bottom: 3px; }
    .note-n { font-weight: 700; }

    .words { margin-top: 12px; font-weight: 700; }

    .bank { margin-top: 10px; }
    .bank__label { font-weight: 700; }

    /* Left blank deliberately: the invoice is signed after printing, or the
       image is dropped in later, so the space is reserved and ruled rather
       than filled. */
    .sign { margin-top: 30px; }
    .sign__field {
      width: 210px;
      height: 46px;
      border-bottom: 1px solid #000;
    }
    .sign__label { color: #1414b8; margin-top: 3px; }

    .regoffice { margin-top: 28px; }
    .regoffice__name { font-weight: 700; }

    /* margin-top:auto pushes this to the foot of the page, not just below
       the content, because .sheet is a full-height flex column. */
    .zoniq-footer {
      margin-top: auto;
      padding-top: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
    }
    /* A credit line, so the label is quiet and the mark leads. */
    .zoniq-footer__label {
      font-size: 7.5px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #94a3b8;
      line-height: 1;
    }
    /* The wordmark file carries ~50% transparent padding top and bottom, so the
       box has to be about twice the ink height we actually want to see. */
    .zoniq-footer__mark { height: 26px; width: auto; object-fit: contain; display: block; }

    @media print {
      body { padding: 0; background: #fff; }
      .sheet { border: 1px solid #000; }
    }
    ${pdfCompactStyles}
  </style>
</head>
<body>
  <div class="invoice-document">
    <div class="sheet">

      <div class="mast">
        <div class="mast__logo">${sellerLogoHtml || `<img src="${logoSrc}" alt="" />`}</div>
        <div class="mast__title">
          <div class="mast__title-main">Tax Invoice</div>
          <div class="mast__title-sub">(Original For Recipient)</div>
        </div>
        <div class="mast__supplier">
          <div class="mast__supplier-name">${sellerName}</div>
          <div>${multilineAddress(options.sellerAddress)}</div>
          <div class="mast__supplier-gstin">GSTIN : ${sellerGstinDisplay}</div>
        </div>
      </div>

      <div class="facts">
        <div class="facts__row facts__row--pair"><span class="facts__label">Number :</span><span>${escapeHtml(invoiceNumberDisplay)}</span></div>
        <div class="facts__row facts__row--pair"><span class="facts__label">Date :</span><span>${invoiceDate}</span></div>
        <div class="facts__row facts__row--pair"><span class="facts__label">Billed To :</span><span>${consigneeNameDisplay}</span></div>
        <div class="facts__row facts__inline">
          <span><span class="facts__label">Event :</span> ${eventNameVal}</span>
          <span><span class="facts__label">Event Date :</span> ${eventDateVal}</span>
          <span><span class="facts__label">Venue :</span> ${eventLocationVal}</span>
          <span><span class="facts__label">Place of Supply :</span> ${placeOfSupply ? escapeHtml(placeOfSupply) : "&nbsp;"}</span>
        </div>
        <div class="facts__row facts__row--pair"><span class="facts__label">GSTIN of Customer :</span><span>${displayOrBlank(options.buyerGstin ?? options.consigneeGstin)}</span></div>
        <div class="facts__row facts__row--pair"><span class="facts__label">GSTIN Customer Name :</span><span>"${buyerNameDisplay}"</span></div>
      </div>

      <div class="currency-line">Currency : INR</div>

      <table class="gst-table">
        <colgroup>
          <col style="width:14%" />
          <col style="width:5%" />
          <col style="width:8.5%" />
          <col style="width:8.5%" />
          <col style="width:8.5%" />
          <col style="width:4%" /><col style="width:8.5%" />
          <col style="width:4%" /><col style="width:8.5%" />
          <col style="width:4%" /><col style="width:8.5%" />
          <col style="width:4%" /><col style="width:5.5%" />
          <col style="width:8.5%" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">Description</th>
            <th rowspan="2">SAC<br/>Code</th>
            <th rowspan="2">Taxable<br/>Value</th>
            <th rowspan="2">NonTaxab<br/>le/Exempt<br/>ed Value</th>
            <th rowspan="2">Total</th>
            <th colspan="2">IGST</th>
            <th colspan="2">CGST</th>
            <th colspan="2">SGST/UGST</th>
            <th colspan="2">CESS</th>
            <th rowspan="2">Total(Incl<br/>Taxes)</th>
          </tr>
          <tr>
            <th>Tax %</th><th>Amount</th>
            <th>Tax %</th><th>Amount</th>
            <th>Tax %</th><th>Amount</th>
            <th>Tax %</th><th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${gstRowsHtml}${emptyGstRow}
          <tr class="gst-total">
            <td class="gst-total-label" colspan="2">Grand Total</td>
            <td class="gst-n">${formatTableAmount(rowTotals.taxable)}</td>
            <td class="gst-n">${formatTableAmount(rowTotals.exempt)}</td>
            <td class="gst-n">${formatTableAmount(grandTaxableTotal)}</td>
            <td class="gst-n" colspan="2">${formatTableAmount(rowTotals.igst)}</td>
            <td class="gst-n" colspan="2">${formatTableAmount(rowTotals.cgst)}</td>
            <td class="gst-n" colspan="2">${formatTableAmount(rowTotals.sgst)}</td>
            <td class="gst-n" colspan="2">0.00</td>
            <td class="gst-n">${formatTableAmount(grandInclTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div class="words">Amount Chargeable (in words) : ${escapeHtml(amountWords)}</div>

      <ul class="notes">
        ${notesHtml}
      </ul>

      ${
        bankRows.length
          ? `<div class="bank"><span class="bank__label">Bank details:</span> ${bankRows.join(" &nbsp;&middot;&nbsp; ")}</div>`
          : ""
      }

      <div class="sign">
        <div class="sign__field"></div>
        <div class="sign__label">Authorized Signatory</div>
      </div>

      <div class="regoffice">
        <div class="regoffice__name">${sellerName}</div>
        <div>${multilineAddress(options.sellerAddress)}</div>
        ${sellerContactLine ? `<div>${sellerContactLine}</div>` : ""}
        ${sellerGstinDisplay ? `<div>GSTIN no. ${sellerGstinDisplay}</div>` : ""}
      </div>

      <div class="zoniq-footer">
        <span class="zoniq-footer__label">Powered by</span>
        <img class="zoniq-footer__mark" src="${logoSrc}" alt="Zoniq" />
      </div>

    </div>
  </div>
</body>
</html>`;
}

export function printInvoiceDocument(
  detail: InvoiceRecord,
  options: InvoicePrintOptions = {}
): boolean {
  const popup = window.open("", "_blank", "width=920,height=780");
  if (!popup) return false;

  popup.document.open();
  popup.document.write(buildInvoicePrintHtml(detail, options));
  popup.document.close();

  const triggerPrint = () => {
    popup.focus();
    popup.print();
  };

  const waitForImages = () => {
    const images = Array.from(popup.document.images);
    if (images.length === 0) {
      triggerPrint();
      return;
    }
    let loaded = 0;
    const onDone = () => {
      loaded += 1;
      if (loaded >= images.length) triggerPrint();
    };
    images.forEach((img) => {
      if (img.complete) onDone();
      else {
        img.addEventListener("load", onDone);
        img.addEventListener("error", onDone);
      }
    });
  };

  if (popup.document.readyState === "complete") waitForImages();
  else popup.addEventListener("load", waitForImages);

  return true;
}

async function resolvePdfOptions(options: InvoicePrintOptions): Promise<InvoicePrintOptions> {
  const logoSrc = await resolveLogoDataUrl();
  const trimmedSellerLogoUrl = options.sellerLogoUrl?.trim();
  // Already resolved to a data: URL (e.g. fetched via an authenticated API
  // call by the caller) — use it as-is instead of re-fetching.
  const sellerLogoUrl = !trimmedSellerLogoUrl
    ? null
    : trimmedSellerLogoUrl.startsWith("data:")
      ? trimmedSellerLogoUrl
      : await resolveExternalImageDataUrl(trimmedSellerLogoUrl);
  return { ...options, logoSrc, sellerLogoUrl, forPdf: true };
}

export async function downloadInvoiceDocument(
  detail: InvoiceRecord,
  options: InvoicePrintOptions = {}
): Promise<void> {
  const html = buildInvoicePrintHtml(detail, await resolvePdfOptions(options));
  const filename = invoiceDownloadFilename(detail.invoice_number || detail.id);
  const pdf = await renderInvoicePdf(html);
  triggerPdfDownload(pdf, filename);
}

/** Generates the invoice PDF and returns it as bytes (Blob) instead of triggering a browser download — used for upload/email/bulk-download flows. */
export async function generateInvoicePdfBlob(
  detail: InvoiceRecord,
  options: InvoicePrintOptions = {}
): Promise<Blob> {
  const html = buildInvoicePrintHtml(detail, await resolvePdfOptions(options));
  const pdf = await renderInvoicePdf(html);
  return pdf.output("blob");
}
